import React, { Component } from 'react';
import {
    number,
    string,
    arrayOf,
    shape,
    oneOfType,
    array,
    func,
    bool,
} from 'prop-types';
import equal from 'fast-deep-equal';
import uniq from 'lodash/uniq';
import compose from 'lodash/flowRight';
import isUndefined from 'lodash/isUndefined';
import classnames from 'classnames';
import { FormattedMessage, injectIntl } from 'react-intl';
import { withColorFamilies, withFiltersConfig } from 'connectors';
import { Tag } from 'components';
import { colorFamiliesShape, filtersConfigShape, intlShape } from 'shapes';
import './Criteria.scss';

class Criteria extends Component {
    static propTypes = {
        className: string,
        // eslint-disable because eslint don't see getDerivedStateFromProps
        // eslint-disable-next-line react/no-unused-prop-types
        items: arrayOf(
            shape({
                label: string,
                attribute: string,
                currentRefinement: oneOfType([
                    string,
                    arrayOf(string),
                    shape({
                        min: number,
                        max: number,
                    }),
                    bool,
                    number,
                ]),
                items: array,
                value: func,
            }),
        ).isRequired,
        theme: shape({
            title: string,
            list: string,
            tag: string,
        }),
        excludedFilters: arrayOf(string), // eslint-disable-line react/no-unused-prop-types
        specificFilters: arrayOf(
            shape({
                label: string,
                type: string,
                order: number,
            }),
        ),
        refine: func.isRequired,
        colorFamilies: colorFamiliesShape.isRequired,
        filtersConfig: filtersConfigShape.isRequired,
        intl: intlShape.isRequired,
    };

    static defaultProps = {
        theme: {},
        excludedFilters: [],
        specificFilters: [],
        className: null,
    };

    static getDerivedStateFromProps(props) {
        return {
            criterias: props.items.filter(
                item =>
                    item.attribute && // added since we forked 'connectCurrentRefinement'
                    item.attribute !== 'urlParams' && // added since we forked 'connectCurrentRefinement'
                    !(
                        typeof item.currentRefinement === 'object' &&
                        Object.keys(item.currentRefinement).length === 0
                    ) && // added since we forked 'connectCurrentRefinement'
                    item.currentRefinement !== 'false' &&
                    !props.excludedFilters.includes(item.attribute),
            ),
        };
    }

    state = {
        criterias: [],
    };

    shouldComponentUpdate(nextProps, nextState) {
        const { className, theme } = this.props;
        const { criterias } = this.state;

        return (
            className !== nextProps.className ||
            theme !== nextProps.theme ||
            criterias.length !== nextState.criterias.length ||
            equal(criterias, nextState.criterias)
        );
    }

    getLabelFromItem = (item, specificFilters = []) => {
        const { filtersConfig, intl } = this.props;
        // for ranges
        if (
            item.currentRefinement &&
            (!isUndefined(item.currentRefinement.min) ||
                !isUndefined(item.currentRefinement.max))
        ) {
            let range;
            const id = item.id.replace('catalog_attribute.', '');
            const label =
                (filtersConfig[item.attribute] &&
                    filtersConfig[item.attribute].labelKey &&
                    filtersConfig[item.attribute].labelKey.label &&
                    intl.formatMessage(
                        filtersConfig[item.attribute].labelKey.label,
                    )) ||
                (specificFilters.find(filter => `${filter.id}` === id) || {})
                    .label ||
                id;

            if (
                isUndefined(item.currentRefinement.min) &&
                !isUndefined(item.currentRefinement.max)
            ) {
                range = `< ${item.currentRefinement.max}`;
            } else if (
                !isUndefined(item.currentRefinement.min) &&
                isUndefined(item.currentRefinement.max)
            ) {
                range = `> ${item.currentRefinement.min}`;
            } else if (
                !isUndefined(item.currentRefinement.min) &&
                !isUndefined(item.currentRefinement.max)
            ) {
                range = `${item.currentRefinement.min} > ${item.currentRefinement.max}`;
            }
            return `${label}: ${range}`;
        }

        if (
            filtersConfig[item.attribute] &&
            filtersConfig[item.attribute].labelKey &&
            filtersConfig[item.attribute].labelKey.label
        ) {
            if (filtersConfig[item.attribute].type === 'bool') {
                return intl.formatMessage(
                    filtersConfig[item.attribute].labelKey.label,
                );
            }
        }
        return item.currentRefinement;
    };

    render() {
        const {
            className,
            theme = {},
            specificFilters,
            colorFamilies,
        } = this.props;
        const { criterias } = this.state;
        if (criterias.length === 0) {
            return null;
        }

        const rootClassnames = classnames('criteria-root', className);
        const titleClassnames = classnames('criteria-root__title', theme.title);
        const tagClassnames = classnames('criteria_root__list__tag', theme.tag);

        return (
            <div className={rootClassnames}>
                <div className={titleClassnames}>
                    <FormattedMessage
                        id="listing.filters.criteria.label"
                        defaultMessage="Your criterias:"
                        description="label of criterias"
                    />
                    <span
                        className="criteria-root__title__length"
                        data-qa="criteriaLengthElm"
                    >
                        <FormattedMessage
                            id="listing.filters.criteria.appliedCount"
                            defaultMessage="{count, plural, one {# filter} other {# filters}}"
                            description="count of applied filters"
                            values={{
                                count: criterias.length,
                            }}
                        />
                    </span>
                </div>
                {criterias.reduce(
                    (reducedCriterias, criteria) =>
                        reducedCriterias.concat(
                            criteria.items && criteria.items.length > 0
                                ? uniq(criteria.items).map(item => (
                                      <Tag
                                          key={`${criteria.attribute}:${item.label}`}
                                          className={tagClassnames}
                                          label={item.label}
                                          value={item.value}
                                          color={colorFamilies.getFamilyColorByName(
                                              `${item.label}`.toLowerCase(),
                                          )}
                                          deletable
                                          dataQa="criteriaTagCta"
                                          onClick={this.props.refine}
                                      />
                                  ))
                                : [
                                      <Tag
                                          key={`${criteria.attribute}:${criteria.currentRefinement}`}
                                          className={tagClassnames}
                                          label={this.getLabelFromItem(
                                              criteria,
                                              specificFilters,
                                          )}
                                          value={criteria.value}
                                          deletable
                                          onClick={this.props.refine}
                                      />,
                                  ],
                        ),
                    [],
                )}
            </div>
        );
    }
}

export default compose(
    withColorFamilies,
    withFiltersConfig,
    injectIntl,
)(Criteria);
